'use client';
import ToolCallChip, { type ToolCallEvent } from './ToolCallChip';
import CitationChip, { type Citation } from './CitationChip';

interface TranscriptsProps {
  transcripts: Array<{ id: string; text: string; participant: string; isFinal: boolean }>;
  toolActivity?: Array<unknown>;
  showToolCalls?: boolean;
  citations?: Array<unknown>;
  showCitations?: boolean;
  /** Same source as Phase 2's idle-background image (`props.avatarThumbnailUrl`
   * from `prepared.widget.defaults`) — reused here as the small circular
   * avatar rendered next to AGENT bubbles only. Null renders a plain colored
   * circle instead of a broken-image icon (no bundled default asset exists
   * for the embed today — see PRD Phase 2). */
  avatarThumbnailUrl?: string | null;
}

// Staggered entrance delay for the Nth rendered item (message, tool chip, or
// citation chip alike), capped so a long transcript can't produce a
// multi-second cascade on first render. Matches the hosted site's
// `Math.min(i * 0.05, 0.3)` pattern (`ChatPanel.tsx` there).
function staggerDelay(index: number): string {
  return `${Math.min(index * 0.05, 0.3)}s`;
}

export default function Transcripts({
  transcripts,
  toolActivity,
  showToolCalls,
  citations,
  showCitations,
  avatarThumbnailUrl,
}: TranscriptsProps) {
  const chips = showToolCalls && toolActivity ? (toolActivity as ToolCallEvent[]) : [];
  const citationChips = showCitations && citations ? (citations as Citation[]) : [];
  const hasContent =
    transcripts.length > 0 || chips.length > 0 || citationChips.length > 0;

  // Global running index across transcripts -> tool chips -> citation chips,
  // in the same order they're rendered below, so the stagger reads as one
  // continuous cascade instead of three separate ones restarting at 0.
  let itemIndex = 0;

  return (
    <div
      className="mobeus-transcripts"
      part="transcripts"
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: 12,
        fontFamily: 'var(--mobeus-font, system-ui, sans-serif)',
        fontSize: 14,
        display: 'flex',
        flexDirection: 'column',
        // Top scroll-fade mask: content scrolling up under the panel header
        // fades out instead of hard-clipping. Values ported from the hosted
        // site's `.chat-messages-container` (`transparent 0%, transparent
        // 70px, black 170px`), scaled down — this panel's header is a plain
        // in-flow `<div>` (~50-60px) rather than a fixed nav floating over
        // the scroll area, so the fade only needs to cover a small band at
        // the very top of the scrollable content itself.
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, transparent 8px, black 28px)',
        maskImage: 'linear-gradient(to bottom, transparent 0, transparent 8px, black 28px)',
      }}
    >
      {/* Scoped to this component only, same pattern Controls.tsx uses for
       * its icon buttons' :hover/:active pseudo-classes (inline styles can't
       * express those). Bubble hover: subtle lift + brightness, no layout
       * shift (shadow only, no transform/scale — a size change here would
       * jostle neighboring bubbles). */}
      <style>{`
        .mobeus-msg {
          transition: box-shadow 0.15s ease, filter 0.15s ease;
        }
        .mobeus-msg:hover {
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
          filter: brightness(1.08);
        }
      `}</style>
      {!hasContent ? (
        <div
          className="mobeus-empty"
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: 'var(--mobeus-text-faint, #9ca3af)',
            fontSize: 13,
            padding: '0 24px',
          }}
        >
          Say hello to start.
        </div>
      ) : (
        <>
          {transcripts.map((t) => {
            const delay = staggerDelay(itemIndex++);
            const isAgent = t.participant === 'agent';
            return (
              <div
                key={t.id}
                className="mobeus-bubble-enter"
                style={{
                  animationDelay: delay,
                  display: 'flex',
                  flexDirection: isAgent ? 'row' : 'row-reverse',
                  alignItems: 'flex-end',
                  gap: 6,
                  marginBottom: 8,
                }}
              >
                {isAgent && (
                  avatarThumbnailUrl ? (
                    <img
                      src={avatarThumbnailUrl}
                      alt=""
                      aria-hidden="true"
                      className="mobeus-msg-avatar"
                      part="message-avatar"
                      style={{
                        width: 24, height: 24, borderRadius: '50%',
                        objectFit: 'cover', flexShrink: 0,
                      }}
                    />
                  ) : (
                    <div
                      className="mobeus-msg-avatar-fallback"
                      part="message-avatar"
                      aria-hidden="true"
                      style={{
                        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                        background: 'var(--mobeus-primary, #3b82f6)',
                      }}
                    />
                  )
                )}
                <div
                  className={isAgent ? 'mobeus-msg mobeus-msg-agent' : 'mobeus-msg mobeus-msg-user'}
                  part={isAgent ? 'message-agent' : 'message-user'}
                  style={{
                    display: 'inline-block', padding: '6px 10px', borderRadius: 12,
                    background: isAgent ? 'var(--mobeus-agent-bubble, #f3f4f6)' : 'var(--mobeus-user-bubble, #3b82f6)',
                    color: isAgent ? 'var(--mobeus-agent-bubble-text, #111)' : 'var(--mobeus-user-bubble-text, #fff)',
                    maxWidth: '80%', opacity: t.isFinal ? 1 : 0.7,
                  }}
                >{t.text}</div>
              </div>
            );
          })}
          {chips.map((event, i) => (
            <div
              key={`tool-${i}-${event.timestamp ?? ''}`}
              className="mobeus-bubble-enter"
              style={{ animationDelay: staggerDelay(itemIndex++) }}
            >
              <ToolCallChip event={event} />
            </div>
          ))}
          {citationChips.length > 0 && (
            <div style={{ marginTop: 4 }}>
              {citationChips.map((c, i) => (
                <div
                  key={`citation-${i}-${
                    (typeof c.source === 'object' && c.source?.document) ||
                    (typeof c.source === 'string' ? c.source : '') ||
                    i
                  }`}
                  className="mobeus-bubble-enter"
                  style={{ display: 'inline-block', animationDelay: staggerDelay(itemIndex++) }}
                >
                  <CitationChip citation={c} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
